import { FC, useEffect, useMemo } from "react";
import { HiLockOpen } from "react-icons/hi2";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { IconPen } from "@/components/_icons/IconPen";
import { HeaderLayout } from "@/components/_layout/header-layout";
import { Button } from "@/components/_ui/button";
import { Link } from "@/components/navigation-controls/link";
import { NotFoundView } from "@/components/not-found-view";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { ClimateActionTitleEditForm } from "@/domain/climate-action/components/climate-action-title-edit-form";
import { useCurrentClimateActionContext } from "@/domain/climate-action/hooks/use-current-climate-action-context";
import { makeClimateActionUrl } from "@/domain/climate-action/lib/make-climate-action-url";
import { CreateProgressSnapshotContextProvider } from "@/domain/progress-snapshot/hooks/use-create-progress-snapshot-context";
import { TeamView } from "@/domain/team/components/team-view";
import { useTeamListForWorkspace } from "@/domain/team/hooks/api/use-team-list-for-workspace";
import { makeTeamUrl } from "@/domain/team/lib/make-team-url";
import { UserFavoriteButton } from "@/domain/user/components/user-favorite-button";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";
import { NavElement } from "@/nav-element";

import { ClimateActionEditDropdown } from "../../../components/climate-action-edit-dropdown";
import { ClimateActionParentLabel } from "../../../components/climate-action-parent-label";
import { ClimateActionSheet } from "../../../components/climate-action-sheet/climate-action-sheet";
import { ClimateActionUIContextProvider, useClimateActionUIState } from "../../../hooks/use-climate-action-ui-state";

const InnerPage: FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser } = useCurrentUserContext();
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { currentClimateAction } = useCurrentClimateActionContext();

  const { activeTeamList } = useTeamListForWorkspace({
    workspaceId: currentWorkspace.id,
    userId: currentUser?.id,
  });

  const location = useLocation();
  const { setShowCreateChildClimateActionView, isEditing, toggleEditViewMode } = useClimateActionUIState();

  useEffect(() => {
    hideCreateChildClimateAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const firstActiveTeamOfUser = useMemo(() => {
    return activeTeamList.find((team) => currentClimateAction?.teamInstanceList.some((ti) => ti.teamId === team.id));
  }, [currentClimateAction, activeTeamList]);

  function showCreateChildClimateAction() {
    if (!pathname.includes("details")) {
      navigate(
        makeClimateActionUrl({ workspace: currentWorkspace, climateAction: currentClimateAction, path: "details" }),
      );
    }
    setTimeout(function () {
      // use setTimeout to make sure the tab is switched before
      // setShowChildClimateActionView is called
      setShowCreateChildClimateActionView(true);
    }, 1);
  }

  function hideCreateChildClimateAction() {
    setShowCreateChildClimateActionView(false);
  }

  return (
    <div className="flex h-full w-full flex-row">
      <div className="flex h-full max-h-full w-full flex-col overflow-y-hidden">
        <HeaderLayout showNavigationControls={true}>
          <div className="flex flex-row items-center gap-2">
            {firstActiveTeamOfUser && (
              <>
                <Link to={makeTeamUrl({ workspace: currentWorkspace, team: firstActiveTeamOfUser, path: "" })}>
                  <TeamView
                    team={{
                      id: firstActiveTeamOfUser.id,
                    }}
                  />
                </Link>
                <div>›</div>
                <Link
                  to={makeTeamUrl({
                    workspace: currentWorkspace,
                    team: firstActiveTeamOfUser,
                    path: "climate-actions",
                  })}
                >
                  <div className="flex max-w-[8rem] flex-row items-center overflow-hidden truncate text-ellipsis whitespace-nowrap">
                    Maßnahmen
                  </div>
                </Link>
              </>
            )}
            {!firstActiveTeamOfUser && (
              <Link to={makeWorkspaceUrl({ workspace: currentWorkspace, path: "climate-actions" })}>
                <div className="flex max-w-[8rem] flex-row items-center overflow-hidden truncate text-ellipsis whitespace-nowrap">
                  Maßnahmen
                </div>
              </Link>
            )}
            <div>›</div>
            <div className="block max-w-[8rem] overflow-hidden truncate text-ellipsis whitespace-nowrap">
              {currentClimateAction?.title !== "" ? (
                currentClimateAction.title
              ) : (
                <span className="text-muted-foreground">Kein Titel</span>
              )}
            </div>
            <div>
              <ClimateActionEditDropdown
                teamId={firstActiveTeamOfUser?.id}
                climateAction={currentClimateAction}
                workspaceId={currentWorkspace.id}
                callbacks={{ showCreateChildClimateAction }}
              />
            </div>
            <UserFavoriteButton pageName={currentClimateAction?.title} />
          </div>
          <div className="mr-4 flex flex-row items-center">
            {canEdit && (
              <Button variant="ghost" size="xs" className="border" onClick={toggleEditViewMode}>
                {isEditing ? (
                  <>
                    <HiLockOpen className="mr-2 h-3 w-3 text-muted-foreground" />
                    Bearbeiten beenden
                  </>
                ) : (
                  <>
                    <IconPen className="mr-2 h-3 w-3 text-muted-foreground" />
                    Bearbeiten
                  </>
                )}
              </Button>
            )}
          </div>
        </HeaderLayout>

        <div className="relative flex w-full flex-row flex-wrap items-center px-6 py-6 text-sm">
          {isEditing && (
            <div className="absolute left-0 right-0 top-0 flex h-4 flex-row items-center justify-center bg-blue-500 text-xs text-background transition-all duration-300 ease-in-out">
              Bearbeitungsmodus
            </div>
          )}

          {!!currentClimateAction?.parentId && (
            <div className="w-full">
              <div className="mb-4">
                <ClimateActionParentLabel
                  climateAction={{ id: currentClimateAction.parentId }}
                  workspace={currentWorkspace}
                />
              </div>
            </div>
          )}

          <ClimateActionTitleEditForm
            climateActionFormData={{
              id: currentClimateAction.id,
              title: currentClimateAction.title ?? "Unbenannte Maßnahme",
            }}
            isEditing={isEditing}
          />

          <div className="flex w-full flex-row items-center gap-2 border-b border-solid py-2 text-sm">
            <NavElement
              to={makeClimateActionUrl({
                workspace: currentWorkspace,
                climateAction: currentClimateAction,
                path: "details",
              })}
              className="flex flex-row items-center [&>span]:h-6 [&>span]:gap-2 [&>span]:border [&>span]:p-2"
              end
            >
              Details
            </NavElement>
            <NavElement
              to={makeClimateActionUrl({
                workspace: currentWorkspace,
                climateAction: currentClimateAction,
                path: "activity",
              })}
              className="flex flex-row items-center [&>span]:h-6 [&>span]:gap-2 [&>span]:border [&>span]:p-2"
              end
            >
              Aktivität
            </NavElement>
            <NavElement
              to={makeClimateActionUrl({
                workspace: currentWorkspace,
                climateAction: currentClimateAction,
                path: "documents",
              })}
              className="flex flex-row items-center [&>span]:h-6 [&>span]:gap-2 [&>span]:border [&>span]:p-2"
              end
            >
              Dokumente
            </NavElement>
          </div>
        </div>

        <div className="overflow-y-scroll pb-24">
          <div className="mx-auto flex grow flex-col items-stretch px-6">
            <Outlet />
          </div>
        </div>
      </div>
      <ClimateActionSheet climateAction={currentClimateAction} />
    </div>
  );
};

export const ClimateActionPage = () => {
  const { climateActionId } = useParams();
  const { hasPermission: hasPermissionToManageClimateAction } = useCheckPermission({
    resource: "climateAction",
    resourceId: climateActionId,
    action: "manage",
  });

  if (!climateActionId) {
    return <NotFoundView />;
  }

  return (
    <ClimateActionUIContextProvider>
      <CreateProgressSnapshotContextProvider>
        <InnerPage canEdit={hasPermissionToManageClimateAction} />
      </CreateProgressSnapshotContextProvider>
    </ClimateActionUIContextProvider>
  );
};
